import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useEPITypes, useCreateEPIInventory, useUpdateEPIInventory, getEPIStatusLabel } from '@/hooks/useRPL';
import { epiInventoryFormSchema, type EPIInventoryFormData } from '@/schemas/rplSchemas';
import type { EPIInventory, EPIStatus } from '@/types/rpl';
import { Loader2 } from 'lucide-react';

interface EPIInventoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  epiInventory?: EPIInventory | null;
}

const STATUSES: EPIStatus[] = ['available', 'assigned', 'expired', 'damaged', 'disposed'];

export function EPIInventoryFormDialog({ open, onOpenChange, epiInventory }: EPIInventoryFormDialogProps) {
  const { data: epiTypes } = useEPITypes(true);
  const createMutation = useCreateEPIInventory();
  const updateMutation = useUpdateEPIInventory();
  const [loading, setLoading] = useState(false);

  const form = useForm<EPIInventoryFormData>({
    resolver: zodResolver(epiInventoryFormSchema),
    defaultValues: {
      epi_type_id: '',
      serial_number: '',
      batch_number: '',
      purchase_date: '',
      expiry_date: '',
      status: 'available',
      supplier: '',
      cost: null,
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        epi_type_id: epiInventory?.epi_type_id || '',
        serial_number: epiInventory?.serial_number || '',
        batch_number: epiInventory?.batch_number || '',
        purchase_date: epiInventory?.purchase_date || '',
        expiry_date: epiInventory?.expiry_date || '',
        status: epiInventory?.status || 'available',
        supplier: epiInventory?.supplier || '',
        cost: epiInventory?.cost || null,
        notes: epiInventory?.notes || '',
      });
    }
  }, [open, epiInventory, form]);

  const onSubmit = async (data: EPIInventoryFormData) => {
    setLoading(true);
    try {
      const payload = {
        epi_type_id: data.epi_type_id,
        serial_number: data.serial_number || null,
        batch_number: data.batch_number || null,
        purchase_date: data.purchase_date || null,
        expiry_date: data.expiry_date || null,
        status: data.status,
        supplier: data.supplier || null,
        cost: data.cost || null,
        notes: data.notes || null,
      };

      if (epiInventory) {
        await updateMutation.mutateAsync({ id: epiInventory.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{epiInventory ? 'Editar EPI' : 'Registrar Nuevo EPI'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="epi_type_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de EPI *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {epiTypes?.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} {type.code && `(${type.code})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="serial_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Numero de serie</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="batch_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lote</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="purchase_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha compra</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiry_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha caducidad</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {getEPIStatusLabel(status)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coste</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="supplier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proveedor</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {epiInventory ? 'Actualizar' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
